/**
 * Shared utilities for parsing evaluation data from the database.
 * Pure functions — no React, no side effects.
 */

import { isUUID } from "@/domain/competency";

/**
 * Parse competency_ids from an evaluation record.
 * Handles both proper JSON arrays and double-encoded strings.
 * Returns a filtered array of valid UUIDs, or empty array.
 */
export function parseCompetencyIds(raw: unknown): string[] {
  let parsed = raw;
  if (typeof parsed === "string") {
    try { parsed = JSON.parse(parsed); } catch { return []; }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((id): id is string => typeof id === "string" && isUUID(id));
}

/**
 * Safely cast survey_answers from a DB record to a typed Record.
 * Returns empty object if null/undefined/non-object.
 */
export function parseSurveyAnswers(raw: unknown): Record<string, number> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, number>;
  }
  return {};
}

/**
 * Default value card gradients used across handout views.
 */
export const VALUE_GRADIENTS = [
  "radial-gradient(110.66% 125.66% at 22.01% 11.41%, #A67BFF 0%, #4B2695 100%)",
  "radial-gradient(99.49% 108.22% at 100% 106.2%, #3EF78B 0%, #249152 100%)",
  "radial-gradient(94.28% 111.64% at 23.58% 29.53%, #F7853E 0%, #914E24 100%)",
  "radial-gradient(107.25% 127.61% at 21.7% 14.39%, #0724FF 0%, #041699 100%)",
  "radial-gradient(110.66% 125.66% at 22.01% 11.41%, #FF6B8A 0%, #951446 100%)",
] as const;

/**
 * Performance level labels (DB enum → display string).
 */
export const PERFORMANCE_LABELS: Record<string, string> = {
  low: "En développement",
  moderate: "Impact modéré",
  high: "Impact exceptionnel",
};
